<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { Check, X } from 'lucide-vue-next';
  import { FormInput, SimpleLoading } from '@/shared/ui';
  import { useLogin } from '../model/useLogin';
  import { useValidation, PASSWORD_RULES } from '../model/useValidation';

  const { handlePasswordChange, isLoginLoading } = useLogin();
  const { validateNewPassword } = useValidation();

  const currentPassword = ref('');
  const newPassword = ref('');
  const confirmPassword = ref('');
  const apiError = ref('');

  const confirmPasswordError = computed(() => {
    if (!confirmPassword.value) return '';
    if (confirmPassword.value !== newPassword.value) return 'As senhas não coincidem';
    return '';
  });

  const isNewPasswordValid = computed(() => validateNewPassword(newPassword.value).length === 0);

  const isFormValid = computed(
    () =>
      currentPassword.value.length > 0 &&
      isNewPasswordValid.value &&
      confirmPassword.value === newPassword.value,
  );

  const handleSubmit = async () => {
    if (!isFormValid.value) return;

    apiError.value = '';
    const error = await handlePasswordChange(currentPassword.value, newPassword.value);
    if (error) {
      apiError.value = error;
    }
  };
</script>
<template>
  <div class="w-2/3">
    <div class="pb-8">
      <h1 class="text-4xl/11 mb-2 font-semibold">
        Altere sua senha
      </h1>
      <p class="text-sm/5 text-gray-03">
        Você precisa alterar sua senha no primeiro acesso
      </p>
    </div>
    <form
      class="flex flex-col gap-6 text-sm/5"
      @submit.prevent="handleSubmit"
    >
      <FormInput
        id="current-password"
        v-model="currentPassword"
        label="Senha atual"
        type="password"
        placeholder="Insira sua senha atual"
        :is-password="true"
      />
      <FormInput
        id="new-password"
        v-model="newPassword"
        label="Nova senha"
        type="password"
        placeholder="Insira sua nova senha"
        :is-password="true"
      />
      <FormInput
        id="confirm-password"
        v-model="confirmPassword"
        label="Confirmar nova senha"
        type="password"
        placeholder="Confirme sua nova senha"
        :error="confirmPasswordError"
        :is-password="true"
      />
      <ul
        class="mt-2 space-y-1"
        data-testid="password-requirements"
      >
        <li
          v-for="rule in PASSWORD_RULES"
          :key="rule.message"
          class="flex items-center gap-2 text-sm"
        >
          <Check
            v-if="rule.test(newPassword)"
            :size="16"
            class="text-green-600"
            data-testid="rule-pass"
          />
          <X
            v-else
            :size="16"
            class="text-red-500"
            data-testid="rule-fail"
          />
          <span>{{ rule.message }}</span>
        </li>
      </ul>
      <p
        v-if="apiError"
        class="text-sm text-red-500"
        data-testid="api-error"
      >
        {{ apiError }}
      </p>
      <button
        type="submit"
        data-testid="change-password-submit-btn"
        class="flex items-center justify-center bg-black text-white rounded-md py-3 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="!isFormValid || isLoginLoading"
      >
        <span v-if="!isLoginLoading">Alterar senha</span>
        <SimpleLoading
          v-else
          :track-color="'white-01'"
        />
      </button>
    </form>
  </div>
</template>
